import axios from 'axios';
import { ReactNode, createContext, useContext } from 'react';

import { TpAssetInfo, TpAssetUpdateInfo, TpAssetRelationInfo, TpAssetPropertyInfo } from '../data/assets/interfaces';
import {
  Api,
  AssetApi,
  TemplateApi,
  DataContextApi,
  TaskApi,
  RelationApi,
  BlobApi,
  UserApi,
  UserOrganization,
  EtlApi,
  PlatformApi,
  ViewStateApi,
} from './models/apis';
import { Asset, Template, TpTemplateInfo, TpTemplateUpdateInfo } from './models/assets';
import { DataContext, DataObject, LogFileData, PresignedBlobData, UploadFileData } from './models/data';
import {
  mapAssetUpdateInfoToPythonAsset,
  mapPythonAssetToAssetInfo,
  mapTemplateInfoToPythonTemplate,
  mapTemplateUpdateInfoToPythonTemplate,
} from './util';
import { FormDefinition, FormDefinitionElement, TaskDefinition, TaskExecutionType } from './models/tasks';
import { useAppDispatch } from '../app/hooks';
import { setTaskExecutions } from '../data/taskExecutionSlice';
import { modifyOpenedAsset } from '../data/assets/queriedAssetsSlice';
import { mapResponseToETLComponentTypeTemplate } from './models/etl';
import { useAuthenticationApi } from '../authentication/authentication';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../utils/compression/base64Utils';
import {
  registerOrganizationChangeReloadListener,
  signalOrganizationChangeAndReload,
} from './organizationChangeReload';

axios.defaults.baseURL = import.meta.env.VITE_API_SERVER_URL;

if (typeof window !== 'undefined') {
  registerOrganizationChangeReloadListener();
}

function usePlatformApi(getSessionId: () => Promise<unknown>): PlatformApi {
  const dispatch = useAppDispatch();

  const asset: AssetApi = {
    listAssets: (params) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        let url = `/assets?limit=${params.limit}&offset=${params.offset}&asc=${params.asc ?? true}`;
        if (params.typeFilter) {
          url += `&asset_type=${params.typeFilter}`;
        }
        if (params.searchText) {
          url += `&search_param=${params.searchText}`;
        }
        if (params.sort) {
          url += `&order_by=${params.sort}`;
        }
        if (params.isSystemAsset !== undefined) {
          url += `&is_system_asset=${params.isSystemAsset}`;
        }
        return axios.get(url, requestConfig).then((response) => {
          const assets: any = [];
          for (const asset of Object.values(response.data.asset_list)) {
            assets.push(mapPythonAssetToAssetInfo(asset));
          }
          return { assets, queryStats: response.data.query_stats };
        });
      });
    },
    addAsset: (asset: Asset) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        const requestData = {
          name: asset.name,
          display_name: asset.displayName,
          type: asset.type,
          properties: asset.properties,
          user_tags: asset.user_tags,
          description: asset.description,
        };
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.post(`/assets`, requestData, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    fetchAsset: (assetId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/assets/${assetId}`, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    fetchAndStoreAsset: (assetId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/assets/${assetId}`, requestConfig).then((response) => {
          const asset = mapPythonAssetToAssetInfo(response.data);
          dispatch(modifyOpenedAsset({ asset }));
        });
      });
    },
    modifyAsset: (assetId: string, assetUpdates: TpAssetUpdateInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        const requestData = mapAssetUpdateInfoToPythonAsset(assetUpdates);
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.patch(`/assets/${assetId}`, requestData, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    setAssetProperty: (assetId: string, propertyInfo: TpAssetPropertyInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.patch(`/assets/${assetId}/set_property`, propertyInfo, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    modifyAssetTemplate: (assetId: string, template: TpTemplateInfo) => {
      return getSessionId().then((headers) => {
        let key: string[];
        if (template.scope === 'dashboard') {
          key = ['templates', 'dashboard_template'];
        } else if (template.scope === 'entity') {
          key = ['templates', 'entity_template'];
        } else {
          throw new Error(`Unsupported template scope: ${template.scope}`);
        }

        const requestData: TpAssetPropertyInfo = {
          key,
          value: template.yamlString,
        };
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.patch(`/assets/${assetId}/set_property`, requestData, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    deleteAsset: (assetId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.delete(`/assets/${assetId}`, requestConfig);
      });
    },

    listAssetRelations: (assetId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/assets/${assetId}/relations`, requestConfig).then((response) => {
          return response.data.relations.map((relation: any) => {
            const isReverse = relation.from_asset.id === assetId;
            const relationObj: TpAssetRelationInfo = {
              id: relation.id,
              type: relation.type,
              toAsset: mapPythonAssetToAssetInfo(isReverse ? relation.to_asset : relation.from_asset),
              isReverse: isReverse,
              fromId: isReverse ? relation.from_asset.id : relation.to_asset.id,
            };
            return relationObj;
          });
        });
      });
    },
    addFilesToAsset: (assetId: string, files: UploadFileData[]) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        const requestData = files.map((file) => {
          return {
            file_name: file.fileName,
            blob_path: file.blobPath,
            destination_property: file.destinationProperty,
            file_type: file.fileType,
          };
        });
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.post(`/assets/${assetId}/files`, requestData, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
    fetchCatalogAsset: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/assets/catalog`, requestConfig).then((response) => {
          return mapPythonAssetToAssetInfo(response.data);
        });
      });
    },
  };

  const relation: RelationApi = {
    addRelation: (fromAssetId: string, toAssetId: string, relationType: string, isReverse: boolean) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        const requestData = {
          from_asset: isReverse ? fromAssetId : toAssetId,
          to_asset: isReverse ? toAssetId : fromAssetId,
          type: relationType,
        };
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.post(`/relations`, requestData, requestConfig).then((response) => {
          const relationObj: TpAssetRelationInfo = {
            id: response.data.id,
            type: response.data.type,
            toAsset: mapPythonAssetToAssetInfo(isReverse ? response.data.to_asset : response.data.from_asset),
            isReverse: isReverse,
            fromId: isReverse ? response.data.from_asset.id : response.data.to_asset.id,
          };
          return relationObj;
        });
      });
    },
    deleteRelation: (relationId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.delete(`/relations/${relationId}`, requestConfig);
      });
    },
  };

  const viewState: ViewStateApi = {
    listViewStates: (assetId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        return axios.get(`/assets/${assetId}/view-states`, requestConfig).then((response) => response.data);
      });
    },
    createViewState: (assetId, request) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        const requestData = {
          ...request,
          compressedState: uint8ArrayToBase64(request.compressedState),
        };
        return axios
          .post(`/assets/${assetId}/view-states`, requestData, requestConfig)
          .then((response) => response.data);
      });
    },
    getViewStateDetail: (stateId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        return axios.get(`/view-states/${stateId}`, requestConfig).then((response) => ({
          ...response.data,
          compressedState: base64ToUint8Array(response.data.compressedState),
        }));
      });
    },
    updateViewState: (stateId, updates) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        return axios.patch(`/view-states/${stateId}`, updates, requestConfig).then((response) => response.data);
      });
    },
    deleteViewState: (stateId) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        return axios.delete(`/view-states/${stateId}`, requestConfig).then(() => undefined);
      });
    },
    listAllViewStatesForOrg: (params) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) requestConfig['headers'] = headers;
        const query = new URLSearchParams({
          limit: String(params.limit),
          offset: String(params.offset),
          asc: String(params.asc ?? true),
        });
        if (params.assetId) query.set('asset_id', params.assetId);
        if (params.createdBy) query.set('created_by', params.createdBy);
        if (params.searchParam) query.set('search_param', params.searchParam);
        if (params.orderBy) query.set('order_by', params.orderBy);
        if (params.assetType) query.set('asset_type', params.assetType);
        return axios.get(`/view-states/admin?${query.toString()}`, requestConfig).then((response) => response.data);
      });
    },
  };

  const template: TemplateApi = {
    fetchTemplates: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/templates`, requestConfig).then((response) => {
          // API returns camel case keys, so we need to map it back to TpTemplateInfo
          const template: Template = {
            ontologies: response.data.ontology,
            assetViews: response.data.asset,
            dashboardTemplates: response.data.dashboard,
            entityViews: response.data.entity,
            startPageViews: response.data.start_page,
            uploadTemplates: response.data.upload,
          };

          return template;
        });
      });
    },
    modifyTemplate: (id: string, changes: TpTemplateUpdateInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = mapTemplateUpdateInfoToPythonTemplate(changes);
        return axios.patch(`/templates/${id}`, requestData, requestConfig).then((response) => {
          // API returns camel case keys, so we need to map it back to TpTemplateInfo
          return response.data;
        });
      });
    },
    deleteTemplate: (templateId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.delete(`/templates/${templateId}`, requestConfig);
      });
    },
    addTemplate: (template: TpTemplateInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = mapTemplateInfoToPythonTemplate(template);

        return axios.post(`/templates`, requestData, requestConfig).then((response) => {
          // API returns camel case keys, so we need to map it back to TpTemplateInfo
          return response.data;
        });
      });
    },
    setDefaultTemplate: (templateId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.post(`/templates/${templateId}/set-default`, {}, requestConfig).then((response) => {
          return response.data as TpTemplateInfo[];
        });
      });
    },
    copyTemplate: (templateId: string, yamlString?: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestBody = yamlString ? { yamlString } : {};
        return axios.post(`/templates/${templateId}/copy`, requestBody, requestConfig).then((response) => {
          return response.data as TpTemplateInfo;
        });
      });
    },
  };

  const dataContext: DataContextApi = {
    getDatacontextData: (datacontext_id: string, assetInfo: TpAssetInfo) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = { params: { datacontext_id: datacontext_id } };
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/data_contexts/get_data_context`, requestConfig).then((response) => {
          if (!response.data) {
            return null;
          }
          const dataContext: DataContext = {
            id: response.data.id,
            assetId: assetInfo.id,
            AssetType: assetInfo.type,
            displayName: response.data.display_name,
            dataObjects: response.data.data_objects.map((d: any) => {
              const dataObject: DataObject = {
                displayName: d.displayName,
                objectType: d.objectType,
                entityType: d.entityType,
                location: d.location,
                publicUrl: d.publicUrl,
                metadataUrl: d.metadataUrl,
                dataContent: d.dataContent ? d.dataContent : '',
                id: d.id,
                tableId: d.identifier,
              };
              return dataObject;
            }),
          };
          return dataContext;
        });
      });
    },
    getDataTablePart: (dataContextId: string, dataObjectIdentifier: string, partIdentifier: string) => {
      return getSessionId().then((headers) => {
        const params = {
          datacontext_id: dataContextId,
          data_object_location: dataObjectIdentifier,
          part_id: partIdentifier,
        };
        const requestConfig: any = { params };
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios
          .get(`/data_contexts/query_data_object`, requestConfig)
          .then((response) => response.data.signed_url);
      });
    },
  };

  const task: TaskApi = {
    createTask: (assetId: string, taskDefinitionId: string, executingParameters: any) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        const requestData: any = {
          task_definition_id: taskDefinitionId,
          executing_parameters: executingParameters,
          parent_asset_id: assetId,
        };
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.post(`/tasks/task_executions`, requestData, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            parentAssetId: response.data.parent_asset_id,
            taskExecutionId: response.data.task_execution_id,
            taskDefinitionId: response.data.task_definition_id,
            executingParameters: response.data.executing_parameters,
            status: response.data.status.toLowerCase(),
            startTime: response.data.start_time,
            endTime: response.data.end_time,
            assets: [],
            logAvailable: response.data.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    fetchTaskDefinitions: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        return axios.get(`/tasks/task_definitions`, requestConfig).then((response) => {
          return response.data.map((task: any) => {
            const formDefinition: FormDefinition = {
              elements: task.formDefinition.elements.map((elem: any) => {
                const formElement: FormDefinitionElement = {
                  id: elem.id,
                  displayName: elem.displayName,
                  required: elem.required,
                  type: elem.type,
                };
                if (elem.helpText) formElement.helpText = elem.helpText;
                if (elem.relationType) formElement.relationType = elem.relationType;
                if (elem.defaultValue) formElement.defaultValue = elem.defaultValue;
                return formElement;
              }),
            };
            const taskDefinition: TaskDefinition = {
              taskDefinitionId: task.taskDefinitionId,
              displayName: task.name,
              description: task.description,
              parentAssetType: task.parentAssetType,
              formDefinition: formDefinition,
              taskType: task.taskType,
            };
            return taskDefinition;
          });
        });
      });
    },
    fecthAndStoreTaskExecutions: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        return axios
          .get(`/tasks/task_executions`, requestConfig)
          .then((response) => {
            return response.data.map((task: any) => {
              const taskExecution: TaskExecutionType = {
                taskExecutionId: task.task_execution_id,
                taskDefinitionId: task.task_definition_id,
                executingParameters: task.executing_parameters,
                status: task.status.toLowerCase(),
                startTime: task.start_time,
                endTime: task.end_time,
                parentAssetId: task.parent_asset_id,
                assets: task.assets
                  ? task.assets.map((asset: any) => {
                      return mapPythonAssetToAssetInfo(asset);
                    })
                  : [],
                logAvailable: task.log_location_available,
              };
              return taskExecution;
            });
          })
          .then((taskExecutionsBeforeSort) => {
            if (taskExecutionsBeforeSort) {
              const taskExecutions = taskExecutionsBeforeSort.sort((a, b) => b.startTime.localeCompare(a.startTime));
              dispatch(setTaskExecutions({ taskExecutions }));
            }
          });
      });
    },
    fetchTaskExecutionById: (taskExecutionId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        return axios.get(`/tasks/task_executions/${taskExecutionId}`, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            taskExecutionId: response.data.task_execution_id,
            taskDefinitionId: response.data.task_definition_id,
            executingParameters: response.data.executing_parameters,
            status: response.data.status.toLowerCase(),
            startTime: response.data.start_time,
            endTime: response.data.end_time,
            parentAssetId: response.data.parent_asset_id,
            assets: response.data.assets ?? [],
            logAvailable: response.data.log_location_available,
          };
          return taskExecution;
        });
      });
    },
    getTaskExecutionLogs: (taskExecutionId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        return axios.get(`/tasks/task_executions/${taskExecutionId}/logs`, requestConfig).then((response) => {
          const preSignedUrlObject: LogFileData = {
            signedUrl: response.data.signed_url,
            blobPath: response.data.blob_path,
          };
          return preSignedUrlObject;
        });
      });
    },
    resumeTaskExecution: (taskExecutionId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        return axios.post(`/tasks/task_executions/${taskExecutionId}/resume`, {}, requestConfig).then((response) => {
          const taskExecution: TaskExecutionType = {
            taskExecutionId: response.data.task_execution_id,
            taskDefinitionId: response.data.task_definition_id,
            executingParameters: response.data.executing_parameters,
            status: response.data.status.toLowerCase(),
            startTime: response.data.start_time,
            endTime: response.data.end_time,
            assets: [],
            logAvailable: response.data.log_location_available,
          };
          return taskExecution;
        });
      });
    },
  };

  const blob: BlobApi = {
    getPresignedUrlForUpload: (file) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = {
          file_name: file.fileName,
          method: file.method,
        };

        return axios.post(`/uploads`, requestData, requestConfig).then((response) => {
          const signedData: UploadFileData = {
            fileName: file.fileName,
            fileType: file.fileType,
            method: file.method,
            signedUrl: response.data.signed_url,
            blobPath: response.data.blob_path,
            id: response.data.id,
          };
          return signedData;
        });
      });
    },
    getPresignedURL: (blobData) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = {
          method: blobData.method,
          blob_path: blobData.blobPath,
          is_download: blobData.isDownload || false,
          is_display: blobData.isDisplay || false,
        };

        return axios.post(`/blob/presigned_url`, requestData, requestConfig).then((response) => {
          const signedData: PresignedBlobData = {
            signedUrl: response.data.signed_url,
            blobPath: response.data.blob_path,
          };
          return signedData;
        });
      });
    },
  };

  const user: UserApi = {
    fetchUser: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/user`, requestConfig).then((response) => {
          const activeOrganization = response.data.organizations.find((org: any) => org.is_active);
          if (!activeOrganization) {
            throw new Error('No active organization found');
          }
          return {
            isOrganizationAdmin: activeOrganization.is_organization_admin,
            canWrite: activeOrganization.can_write,
            acceptedEulaVersion: response.data.accepted_eula_version,
            email: response.data.email,
            isSystemAdmin: response.data.is_system_admin,
            organizations: response.data.organizations
              .map((org: any) => {
                const organization: UserOrganization = {
                  organizationId: org.organization_id,
                  organizationName: org.organization_name,
                  isActive: org.is_active,
                  canWrite: org.can_write,
                  isOrganizationAdmin: org.is_organization_admin,
                };
                return organization;
              })
              .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
          };
        });
      });
    },
    setAcceptedEulaVersion: (acceptedEulaVersion: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = {
          accepted_eula_version: acceptedEulaVersion,
        };
        return axios.put(`/user`, requestData, requestConfig).then((response) => {
          const activeOrganization = response.data.organizations.find((org: any) => org.is_active);
          if (!activeOrganization) {
            throw new Error('No active organization found');
          }
          return {
            isOrganizationAdmin: activeOrganization.is_organization_admin,
            canWrite: activeOrganization.can_write,
            acceptedEulaVersion: response.data.accepted_eula_version,
            isSystemAdmin: response.data.is_system_admin,
            organizations: response.data.organizations
              .map((org: any) => {
                const organization: UserOrganization = {
                  organizationId: org.organization_id,
                  organizationName: org.organization_name,
                  isActive: org.is_active,
                  canWrite: org.can_write,
                  isOrganizationAdmin: org.is_organization_admin,
                };
                return organization;
              })
              .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
          };
        });
      });
    },
    setOrganization: (organizationId: string) => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        const requestData = {
          organization_id: organizationId,
        };
        return axios.put(`/user`, requestData, requestConfig).then((response) => {
          const activeOrganization = response.data.organizations.find((org: any) => org.is_active);
          if (!activeOrganization) {
            throw new Error('No active organization found');
          }
          return {
            isOrganizationAdmin: activeOrganization.is_organization_admin,
            canWrite: activeOrganization.can_write,
            acceptedEulaVersion: response.data.accepted_eula_version,
            isSystemAdmin: response.data.is_system_admin,
            organizations: response.data.organizations
              .map((org: any) => {
                const organization: UserOrganization = {
                  organizationId: org.organization_id,
                  organizationName: org.organization_name,
                  isActive: org.is_active,
                  canWrite: org.can_write,
                  isOrganizationAdmin: org.is_organization_admin,
                };
                return organization;
              })
              .sort((a: UserOrganization, b: UserOrganization) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)),
          };
        });
      });
    },
  };

  const etl: EtlApi = {
    executeFullPipelineForAsset(assetId: string) {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        const requestData = {
          run_mode: 'full',
          components_to_execute: [],
        };
        return axios.post(`/assets/${assetId}/etl_task`, requestData, requestConfig).then((response) => {
          return response.data;
        });
      });
    },
    executePartialPipelineForAsset(assetId: string, modifiedComponents: string[]) {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }

        const requestData = {
          run_mode: 'partial',
          components_to_execute: modifiedComponents,
        };
        return axios.post(`/assets/${assetId}/etl_task`, requestData, requestConfig).then((response) => {
          return response.data;
        });
      });
    },
    fetchComponentTypeTemplates: () => {
      return getSessionId().then((headers) => {
        const requestConfig: any = {};
        if (headers) {
          requestConfig['headers'] = headers;
        }
        return axios.get(`/etl/component_manifests`, requestConfig).then((response) => {
          return response.data.map(mapResponseToETLComponentTypeTemplate);
        });
      });
    },
  };

  return { asset, viewState, relation, template, dataContext, task, blob, user, etl };
}

function useApi(): Api {
  /**
   * instance authentication methods
   * This also instances the hooks use to toggle the identity providers
   * ( `useAuth()` or `useAuth0()` )
   */
  const { getSessionId, logout } = useAuthenticationApi();

  // Whenever a session gets expired or is invalidated, this function properly logs out..
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // If you get this message, it means the user's organization has changed and the session is no longer valid.
      // Reload to obtain the new organization context.
      if (error.response?.status === 403 && error.response?.data?.detail === 'organization_changed') {
        signalOrganizationChangeAndReload();
        return Promise.reject(error);
      }
      const error_responses = ['invalid session', 'session has expired'];
      if (error.response?.status === 403 && error_responses.includes(error.response?.data['detail'])) {
        localStorage.removeItem('sessionId');
        return getSessionId().then((headers) => {
          const config = error.config;
          config.headers['session-id'] = (headers as any)['session-id'];
          return axios.request(config);
        });
      }
      // If you get a 401 error, log out the user, this can be caused by the session being invalid or no active organization
      if (error.response?.status === 401) {
        localStorage.removeItem('sessionId');
        logout(true);
      }
      console.error(error.response?.data?.detail ?? error.message);

      return Promise.reject(error);
    },
  );

  // instance and unpack the PlatformApi methods and return the full API interface object.
  return { ...usePlatformApi(getSessionId), logout };
}

const ApiContext = createContext<Api>({} as Api);

export function ApiProvider({ children }: { children: ReactNode }): JSX.Element {
  return <ApiContext.Provider value={useApi()}>{children}</ApiContext.Provider>;
}

export function useApiContext() {
  return useContext(ApiContext);
}
